import { PenLine, Plus, Trash2, Upload } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppButton from "../../components/ui/AppButton";
import DashboardFilterBar, {
  boolFromToggleFilter,
  isAllFilter,
  STATUS_FILTER_OPTIONS,
} from "../../components/ui/dashboard/DashboardFilterBar";
import Pagination from "../../components/ui/dashboard/Pagination";
import Table, { Column } from "../../components/ui/dashboard/Table";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import DeleteUserModal from "../user/ui/DeleteUserModal";
import {
  CrewMember,
  CrewMemberSearchRequest,
  CrewMemberSearchResponse,
} from "./@types";
import CrewMemberBulkUploadModal from "./components/CrewMemberBulkUploadModal";
import CrewMemberModal from "./CrewMemberModal";
import { useGetCrewMembersMutation } from "./driverApiSlice";

const CrewMembers: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [selectedCM, setSelectedCM] = useState<CrewMember | null>(null);
  const [openModal, setOpenModal] = useState("");
  const debouncedKeyword = useDebouncedValue(searchTerm.trim());

  const [getCrewMember, { data: driverData, isLoading, error: loadError }] =
    useGetCrewMembersMutation();

  const fetchCM = useCallback(async () => {
    const requestBody: CrewMemberSearchRequest = {
      pageNumber: currentPage,
      pageSize,
      keyword: debouncedKeyword || undefined,
      isActive: boolFromToggleFilter(statusFilter, "active", "inactive"),
    };

    try {
      await getCrewMember(requestBody).unwrap();
    } catch (err) {
      console.error("Failed to fetch crew members:", err);
    }
  }, [currentPage, pageSize, debouncedKeyword, statusFilter, getCrewMember]);

  useEffect(() => {
    fetchCM();
  }, [fetchCM]);

  useEffect(() => {
    setCurrentPage(0);
  }, [debouncedKeyword, statusFilter]);

  // Table columns configuration
  const columns: Column<CrewMember>[] = [
    {
      key: "fullName",
      header: "Name",
      width: "",
      render: (value: string, item: CrewMember) => (
        <span className="text-hColor capitalize">{item.fullName}</span>
      ),
    },
    {
      key: "phoneNumber",
      header: "Phone number",

      render: (value: string) => (
        <span className="text-hColor capitalize">{value}</span>
      ),
    },
    {
      key: "department",
      header: "Department",

      render: (value: string) => <span className="text-hColor">{value}</span>,
    },

    {
      key: "actions",
      header: "Action",

      align: "center" as const,
      render: (value: any, item: CrewMember) => {
        return (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setSelectedCM(item);
                setOpenModal("delete");
              }}
              className="p-1 rounded hover:bg-red-50 text-red-600"
              aria-label="Delete crew member"
            >
              <Trash2 className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => {
                setSelectedCM(item);
                setOpenModal("edit");
              }}
              className="p-1 rounded hover:bg-gray-100 text-hColor"
              aria-label="Edit crew member"
            >
              <PenLine className="w-4 h-4" />
            </button>
          </div>
        );
      },
    },
  ];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(0);
  };

  const handleModal = () => {
    setOpenModal("");
    fetchCM();
  };

  const activeFilters = useMemo(() => {
    const chips: { key: string; label: string; onRemove: () => void }[] = [];
    if (debouncedKeyword) {
      chips.push({
        key: "keyword",
        label: `Search: ${debouncedKeyword}`,
        onRemove: () => setSearchTerm(""),
      });
    }
    if (!isAllFilter(statusFilter)) {
      chips.push({
        key: "status",
        label: `Status: ${STATUS_FILTER_OPTIONS.find((o) => o.value === statusFilter)?.label}`,
        onRemove: () => setStatusFilter("all"),
      });
    }
    return chips;
  }, [debouncedKeyword, statusFilter]);

  const response = driverData?.data as CrewMemberSearchResponse | undefined;

  return (
    <div className="min-h-screen py-6 w-full bg-[#F9F9F9]">
      <div className="max-w-full lg:max-w-[80vw] mx-auto space-y-10">
        <div className="flex flex-wrap items-center justify-between gap-3 bg-white rounded-[10px] p-4">
          <div className="">
            <h2 className="text-xl font-semibold text-hColor">Crew members</h2>
          </div>

          <div className="flex gap-3">
            <AppButton
              appearance="outline"
              onClick={() => setOpenModal("bulk")}
              className="flex items-center px-4 py-2 rounded-lg w-auto h-[38px]"
            >
              <Upload className="h-4 w-4 mr-2" />
              Bulk upload
            </AppButton>
            <AppButton
              onClick={() => {
                setSelectedCM(null);
                setOpenModal("add");
              }}
              className="flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/60 transition-colors h-[38px]"
            >
              <Plus className="h-4 w-4 mr-2" />
              Crew member
            </AppButton>
          </div>
        </div>

        {/* Crew members section */}
        <div className="space-y-4 bg-white rounded-[10px] p-4">
          <DashboardFilterBar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            onClearSearch={() => setSearchTerm("")}
            searchPlaceholder="Search by name, email or phone"
            isLoading={isLoading}
            filters={[
              {
                id: "status",
                placeholder: "Status",
                value: statusFilter,
                options: STATUS_FILTER_OPTIONS,
                onChange: (value) => setStatusFilter(value || "all"),
              },
            ]}
            chips={activeFilters}
            onClearAll={() => {
              setSearchTerm("");
              setStatusFilter("all");
            }}
            resultCount={response?.totalCount}
            resultLabel={
              response?.totalCount === 1 ? "crew member" : "crew members"
            }
          />
            <Table
              data={response?.data || []}
              columns={columns}
              loading={isLoading}
              error={loadError}
              emptyMessage="No crew members found"
            />
        </div>

        <div className="">
          {/* Pagination */}
          {response && (
            <Pagination
              currentPage={response.currentPage}
              totalPages={response.totalPages}
              pageSize={response.pageSize}
              totalCount={response.totalCount}
              hasNextPage={response.hasNextPage}
              hasPreviousPage={response.hasPreviousPage}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          )}
        </div>
      </div>

      <CrewMemberModal
        isOpen={openModal === "add" || openModal === "edit"}
        onClose={handleModal}
        crewMember={openModal === "edit" ? selectedCM : null}
      />

      <CrewMemberBulkUploadModal
        isOpen={openModal === "bulk"}
        onClose={handleModal}
      />

      <DeleteUserModal
        isOpen={openModal === "delete"}
        onClose={handleModal}
        userId={selectedCM?.userId || selectedCM?.id}
        entityLabel="crew member"
      />
    </div>
  );
};

export default CrewMembers;
